/**
 * West Coast Deals - UI Enhancements
 * Toast Notifications, Cart Drawer, Optimistic Updates
 */

(function() {
  'use strict';

  // ========================================
  // Toast Notification System
  // ========================================
  const Toast = {
    container: null,
    
    init() {
      this.container = document.getElementById('toast-container');
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        this.container.className = 'toast-container';
        this.container.setAttribute('aria-live', 'polite');
        this.container.setAttribute('aria-atomic', 'true');
        document.body.appendChild(this.container);
      }
    },
    
    show(options) {
      const {
        type = 'info',
        title = '',
        message = '',
        duration = 4000,
        action = null
      } = options;
      
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.setAttribute('role', 'alert');
      
      const icons = {
        success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>',
        error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>',
        warning: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>',
        info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>'
      };
      
      toast.innerHTML = `
        <span class="toast-icon">${icons[type]}</span>
        <div class="toast-content">
          ${title ? `<div class="toast-title">${title}</div>` : ''}
          ${message ? `<div class="toast-message">${message}</div>` : ''}
          ${action ? `<div class="toast-action"><button class="toast-action-btn">${action.text}</button></div>` : ''}
        </div>
        <button class="toast-close" aria-label="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      `;
      
      this.container.appendChild(toast);
      
      // Trigger animation
      requestAnimationFrame(() => {
        toast.classList.add('toast-visible');
      });
      
      // Close button
      const closeBtn = toast.querySelector('.toast-close');
      closeBtn.addEventListener('click', () => this.dismiss(toast));
      
      // Action button
      if (action && action.onClick) {
        const actionBtn = toast.querySelector('.toast-action-btn');
        actionBtn.addEventListener('click', () => {
          action.onClick();
          this.dismiss(toast);
        });
      }
      
      // Auto dismiss
      if (duration > 0) {
        setTimeout(() => this.dismiss(toast), duration);
      }
      
      return toast;
    },
    
    dismiss(toast) {
      if (!toast || !toast.parentNode) return;
      
      toast.classList.remove('toast-visible');
      toast.classList.add('toast-exiting');
      
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 500);
    },
    
    success(title, message, options = {}) {
      return this.show({ type: 'success', title, message, ...options });
    },
    
    error(title, message, options = {}) {
      return this.show({ type: 'error', title, message, duration: 6000, ...options });
    },
    
    warning(title, message, options = {}) {
      return this.show({ type: 'warning', title, message, ...options });
    },
    
    info(title, message, options = {}) {
      return this.show({ type: 'info', title, message, ...options });
    }
  };

  // ========================================
  // Cart Drawer
  // ========================================
  const CartDrawer = {
    drawer: null,
    overlay: null,
    isOpen: false,
    
    init() {
      this.createDrawer();
      this.bindEvents();
    },
    
    createDrawer() {
      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'cart-drawer-overlay';
      document.body.appendChild(this.overlay);
      
      // Create drawer
      this.drawer = document.createElement('div');
      this.drawer.className = 'cart-drawer';
      this.drawer.innerHTML = `
        <div class="cart-drawer-header">
          <h2 class="cart-drawer-title">Your Cart <span class="cart-drawer-count"></span></h2>
          <button class="cart-drawer-close" aria-label="Close cart">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="cart-drawer-items"></div>
        <div class="cart-drawer-footer">
          <div class="cart-drawer-subtotal">
            <span class="cart-drawer-subtotal-label">Subtotal</span>
            <span class="cart-drawer-subtotal-value"></span>
          </div>
          <p class="cart-drawer-note">Shipping and taxes calculated at checkout</p>
          <button class="cart-drawer-checkout">Proceed to Checkout</button>
          <a href="/collections/all" class="cart-drawer-continue">Continue Shopping</a>
        </div>
      `;
      document.body.appendChild(this.drawer);
    },
    
    bindEvents() {
      // Close button
      const closeBtn = this.drawer.querySelector('.cart-drawer-close');
      closeBtn.addEventListener('click', () => this.close());
      
      // Overlay click
      this.overlay.addEventListener('click', () => this.close());
      
      // Continue shopping
      const continueBtn = this.drawer.querySelector('.cart-drawer-continue');
      continueBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
      
      // Checkout button
      const checkoutBtn = this.drawer.querySelector('.cart-drawer-checkout');
      checkoutBtn.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
      
      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
      
      // Cart icon click - open drawer instead of going to cart page
      const cartIcons = document.querySelectorAll('.cart-icon');
      cartIcons.forEach(icon => {
        icon.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });
    },
    
    async open() {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      this.overlay.classList.add('active');
      this.drawer.classList.add('active');
      
      await this.refresh();
    },
    
    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
      this.overlay.classList.remove('active');
      this.drawer.classList.remove('active');
    },
    
    async refresh() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.render(cart);
      } catch (error) {
        console.error('Error fetching cart:', error);
        Toast.error('Error', 'Could not load cart');
      }
    },
    
    render(cart) {
      const itemsContainer = this.drawer.querySelector('.cart-drawer-items');
      const countEl = this.drawer.querySelector('.cart-drawer-count');
      const subtotalEl = this.drawer.querySelector('.cart-drawer-subtotal-value');
      const footer = this.drawer.querySelector('.cart-drawer-footer');
      
      // Update count
      countEl.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
      
      // Update subtotal
      subtotalEl.textContent = formatMoney(cart.total_price, window.moneyFormat || '${{amount}}');
      
      if (cart.items.length === 0) {
        itemsContainer.innerHTML = `
          <div class="cart-drawer-empty">
            <div class="cart-drawer-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
              </svg>
            </div>
            <h3 class="cart-drawer-empty-title">Your cart is empty</h3>
            <p class="cart-drawer-empty-message">Start shopping to add items to your cart</p>
          </div>
        `;
        footer.style.display = 'none';
      } else {
        footer.style.display = 'block';
        itemsContainer.innerHTML = cart.items.map((item, index) => `
          <div class="cart-drawer-item" data-line="${index + 1}" data-key="${item.key}">
            <div class="cart-drawer-item-image">
              <img src="${item.image ? item.image.replace(/(\.[^.]+)$/, '_200x200$1') : ''}" alt="${item.title}">
            </div>
            <div class="cart-drawer-item-details">
              <h4 class="cart-drawer-item-title">${item.product_title}</h4>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer-item-variant">${item.variant_title}</p>` : ''}
              <p class="cart-drawer-item-price">${formatMoney(item.final_line_price, window.moneyFormat || '${{amount}}')}</p>
              <div class="cart-drawer-item-actions">
                <div class="quantity-wrapper">
                  <button class="quantity-btn quantity-minus" data-line="${index + 1}" aria-label="Decrease quantity">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
                  </button>
                  <span class="quantity-display">${item.quantity}</span>
                  <button class="quantity-btn quantity-plus" data-line="${index + 1}" aria-label="Increase quantity">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
                  </button>
                </div>
                <button class="cart-drawer-item-remove" data-line="${index + 1}">Remove</button>
              </div>
            </div>
          </div>
        `).join('');
        
        // Bind item events
        this.bindItemEvents();
      }
      
      // Update cart counts in header
      const cartCounts = document.querySelectorAll('.cart-count');
      cartCounts.forEach(el => {
        el.textContent = cart.item_count;
      });
    },
    
    bindItemEvents() {
      // Quantity buttons
      this.drawer.querySelectorAll('.quantity-minus').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = parseInt(e.currentTarget.dataset.line);
          const item = e.currentTarget.closest('.cart-drawer-item');
          const currentQty = parseInt(item.querySelector('.quantity-display').textContent);
          if (currentQty > 1) {
            this.updateQuantity(line, currentQty - 1, item);
          }
        });
      });
      
      this.drawer.querySelectorAll('.quantity-plus').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = parseInt(e.currentTarget.dataset.line);
          const item = e.currentTarget.closest('.cart-drawer-item');
          const currentQty = parseInt(item.querySelector('.quantity-display').textContent);
          this.updateQuantity(line, currentQty + 1, item);
        });
      });
      
      // Remove buttons
      this.drawer.querySelectorAll('.cart-drawer-item-remove').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const line = parseInt(e.currentTarget.dataset.line);
          const item = e.currentTarget.closest('.cart-drawer-item');
          this.removeItem(line, item);
        });
      });
    },
    
    async updateQuantity(line, quantity, itemEl) {
      // Optimistic update
      const qtyDisplay = itemEl.querySelector('.quantity-display');
      const oldQty = qtyDisplay.textContent;
      qtyDisplay.textContent = quantity;
      
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        
        const cart = await response.json();
        this.render(cart);
        
        // Animate cart count
        const cartCounts = document.querySelectorAll('.cart-count');
        cartCounts.forEach(el => {
          el.classList.add('bump');
          setTimeout(() => el.classList.remove('bump'), 400);
        });
        
      } catch (error) {
        console.error('Error updating cart:', error);
        qtyDisplay.textContent = oldQty;
        Toast.error('Error', 'Could not update quantity');
      }
    },
    
    async removeItem(line, itemEl) {
      // Optimistic update - animate out
      itemEl.classList.add('removing');
      
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 })
        });
        
        const cart = await response.json();
        
        setTimeout(() => {
          this.render(cart);
          Toast.success('Removed', 'Item removed from cart');
        }, 350);
        
      } catch (error) {
        console.error('Error removing item:', error);
        itemEl.classList.remove('removing');
        Toast.error('Error', 'Could not remove item');
      }
    },
    
    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }]
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to add item');
        }
        
        const data = await response.json();
        
        // Update cart counts with animation
        const cartCounts = document.querySelectorAll('.cart-count');
        cartCounts.forEach(el => {
          const newCount = parseInt(el.textContent) + quantity;
          el.textContent = newCount;
          el.classList.add('bump');
          setTimeout(() => el.classList.remove('bump'), 400);
        });
        
        Toast.success('Added to Cart', 'Item has been added to your cart', {
          action: {
            text: 'View Cart',
            onClick: () => this.open()
          }
        });
        
        // Open drawer
        this.open();
        
        return data;
        
      } catch (error) {
        console.error('Error adding to cart:', error);
        Toast.error('Error', 'Could not add item to cart');
        throw error;
      }
    }
  };

  // ========================================
  // Initialize on DOM Ready
  // ========================================
  document.addEventListener('DOMContentLoaded', function() {
    Toast.init();
    CartDrawer.init();
  });

  // ========================================
  // Expose to Global Scope
  // ========================================
  window.WCD = window.WCD || {};
  window.WCD.Toast = Toast;
  window.WCD.CartDrawer = CartDrawer;

})();
